import React from "react";
import { Link } from "react-router-dom";
import { brand as ds } from "../designSystem";

/* ════════════════════════════════════════════════════════════════════════
   The shared shell for every legal / transactional page: AGB, Widerruf,
   Kündigung, Impressum, Datenschutz, Success.

   These pages carry the most weight for whether the product looks like a
   business or a hobby page, and they used to be the least consistent part
   of the site — five hand-rolled dark cards, each with its own heading
   treatment, its own uppercase tracking and its own paddings.

   One shell, the tokens from designSystem.js, and the same footer nav on
   all of them. Prose sits at `ds.reading` (720px) so lines stay readable.

   Bewusst schlicht: keine Karten, keine grauen Kästen. Ein Rechtstext soll
   aussehen wie ein Rechtstext — Überschrift, Absatz, Überschrift.
   ════════════════════════════════════════════════════════════════════════ */

export const H2 = ({ children, id }) => (
  <h2
    id={id}
    style={{
      fontFamily: ds.font.body,
      fontWeight: 600,
      fontSize: 20,
      lineHeight: 1.35,
      color: ds.color.ink,
      margin: "0 0 12px",
      scrollMarginTop: 32,
    }}
  >
    {children}
  </h2>
);

export const H3 = ({ children, id }) => (
  <h3
    id={id}
    style={{
      fontFamily: ds.font.body,
      fontWeight: 600,
      fontSize: 17,
      lineHeight: 1.4,
      color: ds.color.ink,
      margin: "20px 0 8px",
      scrollMarginTop: 32,
    }}
  >
    {children}
  </h3>
);

export const P = ({ children, muted, style }) => (
  <p
    style={{
      fontFamily: ds.font.body,
      fontWeight: 400,
      fontSize: 17,
      lineHeight: 1.65,
      color: muted ? ds.color.muted : ds.color.ink,
      margin: "0 0 14px",
      ...style,
    }}
  >
    {children}
  </p>
);

/* Ein Abschnitt: nur Abstand, keine Fläche. */
export const Section = ({ children, id, style }) => (
  <section
    id={id}
    style={{
      marginBottom: 40,
      scrollMarginTop: 24,
      ...style,
    }}
  >
    {children}
  </section>
);

/* Ein Unterblock innerhalb eines Abschnitts — ohne Hintergrund. */
export const Well = ({ children, style }) => (
  <div style={{ margin: "0 0 14px", ...style }}>{children}</div>
);

/* Roter Hinweis im Fließtext — nur Information, nie ein zusätzlicher
   Schritt: Zwischen Kündigungs-/Widerrufsbutton und Bestätigung erlauben
   § 312k und § 356a BGB keine Rückfragen oder Pop-ups. */
export const Warnung = ({ children, style }) => (
  <P
    style={{
      background: ds.color.dangerTint,
      color: ds.color.danger,
      borderRadius: ds.radius.md,
      padding: "14px 16px",
      fontWeight: 600,
      ...style,
    }}
  >
    {children}
  </P>
);

/* Aufzählung im Fließtext. */
export const List = ({ items }) => (
  <ul className="ds-list">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

export default function LegalPage({ title, intro, updated, children, wide }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: ds.color.canvas,
        padding: `clamp(40px, 8vw, ${ds.space[24]}px) clamp(20px, 5vw, 40px) ${ds.space[16]}px`,
      }}
    >
      <div style={{ maxWidth: wide ? ds.container : ds.reading, margin: "0 auto" }}>
        {/* Header */}
        <header style={{ paddingBottom: 40 }}>
          <Link
            to="/"
            className="sb-link"
            style={{
              fontFamily: ds.font.body,
              fontSize: 14,
              fontWeight: 600,
              color: ds.color.muted,
              textDecoration: "none",
            }}
          >
            ← Schriftbot
          </Link>
          <h1
            style={{
              fontFamily: ds.font.display,
              fontWeight: 400,
              fontSize: ds.text.h1,
              lineHeight: ds.leading.tight,
              letterSpacing: ds.tracking.tight,
              color: ds.color.ink,
              margin: "20px 0 0",
            }}
          >
            {title}
          </h1>
          {intro && (
            <p
              style={{
                fontFamily: ds.font.body,
                fontSize: 18,
                lineHeight: 1.65,
                color: ds.color.muted,
                margin: "16px 0 0",
              }}
            >
              {intro}
            </p>
          )}
        </header>

        {children}

        {/* Footer nav — identical on every legal page */}
        <footer
          style={{
            paddingTop: 32,
            borderTop: `1px solid ${ds.color.line}`,
            display: "flex",
            flexWrap: "wrap",
            gap: "12px 24px",
            alignItems: "center",
            fontFamily: ds.font.body,
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          <Link to="/" className="sb-link">
            Startseite
          </Link>
          <Link to="/agb" className="sb-link">
            AGB
          </Link>
          <Link to="/widerruf" className="sb-link">
            Widerruf
          </Link>
          <Link to="/kuendigen" className="sb-link">
            Kündigen
          </Link>
          <Link to="/datenschutz" className="sb-link">
            Datenschutz
          </Link>
          <Link to="/impressum" className="sb-link">
            Impressum
          </Link>
          {updated && (
            <span style={{ color: ds.color.subtle, fontWeight: 400 }}>
              Stand: {updated}
            </span>
          )}
        </footer>
      </div>
    </div>
  );
}
